// ErrorsLearning.jsx — Errors & Learning module: log mistakes, capture lessons,
// learn patterns with AI. Cross-linked with campaigns / meetings / events / products.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatErr } from "./lib/api";
import { useAuth } from "./lib/auth";
import { confirmWithUndo, showToast } from "./lib/theme";
import { downloadCSV } from "./lib/export";
import { AssigneeSelect } from "./lib/users";
import DateField from "./components/DateField";
import { InfoTip } from "./components/InfoTip";
import { formatKSA } from "./lib/ksaTime";

const CATS = {
  operational: { lbl: "🛠 تشغيلي", color: "#3b82f6" },
  managerial:  { lbl: "👔 إداري", color: "#8b5cf6" },
  marketing:   { lbl: "📣 تسويقي", color: "#ec4899" },
  inventory:   { lbl: "📦 مخزون", color: "#f59e0b" },
  customer:    { lbl: "🙋 تجربة عميل", color: "#10b981" },
  financial:   { lbl: "💰 مالي", color: "#ef4444" },
  other:       { lbl: "📌 أخرى", color: "#6b7280" },
};
const SEVS = {
  low:      { lbl: "بسيط", color: "#10b981" },
  medium:   { lbl: "متوسط", color: "#f59e0b" },
  high:     { lbl: "عالي", color: "#ef4444" },
  critical: { lbl: "حرج", color: "#991b1b" },
};
const STATUSES = {
  open:          { lbl: "🔴 مفتوح", color: "#ef4444" },
  investigating: { lbl: "🔍 قيد التحقيق", color: "#f59e0b" },
  resolved:      { lbl: "✅ تم الحل", color: "#10b981" },
  monitoring:    { lbl: "👁 تحت المراقبة", color: "#3b82f6" },
  closed:        { lbl: "📦 مغلق", color: "#6b7280" },
};

function fm(n) { return Math.round(Number(n) || 0).toLocaleString("en-US"); }

const EMPTY_FORM = {
  title: "",
  date: new Date().toISOString().slice(0, 10),
  category: "operational",
  severity: "medium",
  description: "",
  root_cause: "",
  lesson_learned: "",
  preventive_action: "",
  owner: "",
  status: "open",
  linked_shift: "",
  linked_product: "",
  cost_impact_sar: 0,
  tags: "",
};

export default function ErrorsLearning() {
  const { user } = useAuth();
  const canCreate = true;            // Open submission to all logged-in users
  const canEdit = user.role === "admin";  // Editing/deleting locked to admin (audit-trail integrity)
  const canDelete = user.role === "admin";
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState({ category: "", severity: "", status: "", q: "" });
  const [view, setView] = useState("list"); // list | knowledge | ai
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiGeneratedAt, setAiGeneratedAt] = useState("");
  const [knowledge, setKnowledge] = useState([]);
  const [meetingsList, setMeetingsList] = useState([]);
  const [similar, setSimilar] = useState(null); // {matches:[], ai_advice:""}
  const [similarBusy, setSimilarBusy] = useState(false);

  const findSimilar = useCallback(async () => {
    if (!form.title.trim() && !form.description.trim()) {
      showToast({ kind: "info", message: "اكتب العنوان أو الوصف أولاً" });
      return;
    }
    setSimilarBusy(true);
    try {
      const { data } = await api.post("/errors/similar-lessons", {
        title: form.title,
        description: form.description,
        category: form.category,
      });
      setSimilar(data);
      if ((data.matches || []).length === 0) {
        showToast({ kind: "info", message: "لا توجد دروس مشابهة — هذا خطأ جديد!" });
      } else {
        showToast({ kind: "success", message: `🔎 وُجدت ${data.matches.length} دروس مشابهة` });
      }
    } catch (e) {
      showToast({ kind: "danger", message: formatErr(e.response?.data?.detail) || e.message });
    } finally { setSimilarBusy(false); }
  }, [form.title, form.description, form.category]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/errors");
      setItems(data.items || []);
      setStats(data.stats || null);
    } finally { setLoading(false); }
  }, []);

  const loadAILatest = useCallback(async () => {
    try {
      const { data } = await api.get("/errors/ai-analysis/latest");
      if (data?.text) {
        setAiText(data.text);
        setAiGeneratedAt(data.generated_at || "");
      }
    } catch (_) { /* ignore */ }
  }, []);

  const loadKnowledge = useCallback(async () => {
    try {
      const { data } = await api.get("/errors/knowledge");
      setKnowledge(data.items || []);
    } catch (_) { /* ignore */ }
  }, []);

  const loadMeetings = useCallback(async () => {
    try {
      const { data } = await api.get("/meetings");
      setMeetingsList(data.items || []);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { load(); loadAILatest(); loadKnowledge(); loadMeetings(); }, [load, loadAILatest, loadKnowledge, loadMeetings]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      showToast({ kind: "danger", message: "عنوان الخطأ مطلوب" });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        cost_impact_sar: Number(form.cost_impact_sar) || 0,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editingId) {
        await api.patch(`/errors/${editingId}`, payload);
        showToast({ kind: "success", message: "✅ تم تحديث الخطأ" });
      } else {
        await api.post("/errors", payload);
        showToast({ kind: "success", message: `✅ تم تسجيل خطأ "${form.title}" — جزء من المعرفة الآن` });
      }
      setForm(EMPTY_FORM);
      setShowForm(false);
      setEditingId(null);
      load(); loadKnowledge();
    } catch (e) {
      showToast({ kind: "danger", message: formatErr(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const startEdit = (it) => {
    setForm({
      ...EMPTY_FORM,
      ...it,
      cost_impact_sar: it.cost_impact_sar || 0,
      tags: (it.tags || []).join(", "),
      linked_shift: it.linked_shift || "",
      linked_product: it.linked_product || "",
      owner: it.owner || "",
    });
    setEditingId(it.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = (it) => {
    setItems((cur) => cur.filter((x) => x.id !== it.id));
    confirmWithUndo({
      message: `🗑 سيتم حذف "${it.title}"`,
      onConfirm: async () => { try { await api.delete(`/errors/${it.id}`); } catch (_) { /* ignore */ } load(); },
      onUndo: load,
    });
  };

  const setStatus = async (it, status) => {
    await api.patch(`/errors/${it.id}`, { status });
    load();
    showToast({ kind: "info", message: `تم تحديث الحالة → ${STATUSES[status]?.lbl}` });
  };

  const convertToTask = async (it) => {
    const mid = window.prompt(
      "اختر اجتماعاً لإضافة هذا الخطأ كمهمة:\n" +
      meetingsList.map((m, i) => `${i + 1}. ${m.title} (${m.date})`).join("\n") +
      "\n\nأدخل الرقم:"
    );
    const idx = parseInt(mid, 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= meetingsList.length) {
      showToast({ kind: "danger", message: "اختيار غير صحيح" });
      return;
    }
    try {
      await api.post(`/errors/${it.id}/convert-to-task`, {
        meeting_id: meetingsList[idx].id,
      });
      showToast({ kind: "success", message: `✅ أُضيف كمهمة في اجتماع "${meetingsList[idx].title}"` });
      load();
    } catch (e) {
      showToast({ kind: "danger", message: formatErr(e.response?.data?.detail) });
    }
  };

  const runAI = async () => {
    if (items.length === 0) {
      showToast({ kind: "danger", message: "لا توجد أخطاء مسجّلة. سجّل بعض الأخطاء أولاً." });
      return;
    }
    setAiBusy(true);
    setAiText("");
    try {
      const { data } = await api.post("/errors/ai-analyze");
      setAiText(data.text || "");
      setAiGeneratedAt(data.generated_at || "");
      showToast({ kind: "success", message: `🧠 تم التحليل (${data.errors_analyzed} خطأ)` });
    } catch (e) {
      showToast({ kind: "danger", message: formatErr(e.response?.data?.detail) || e.message });
    } finally { setAiBusy(false); }
  };

  const exportAll = () => {
    const rows = items.map((it) => [
      it.date, it.title, CATS[it.category]?.lbl || it.category, SEVS[it.severity]?.lbl || it.severity,
      STATUSES[it.status]?.lbl || it.status, it.description || "", it.root_cause || "",
      it.lesson_learned || "", it.preventive_action || "", it.owner || "",
      it.cost_impact_sar || 0, (it.tags || []).join("; "),
    ]);
    downloadCSV("الأخطاء_والدروس_المستفادة",
      ["التاريخ", "العنوان", "التصنيف", "الخطورة", "الحالة", "الوصف", "السبب الجذري",
       "الدرس المستفاد", "الإجراء الوقائي", "المسؤول", "كلفة (ر)", "وسوم"], rows);
  };

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filter.category && it.category !== filter.category) return false;
      if (filter.severity && it.severity !== filter.severity) return false;
      if (filter.status && it.status !== filter.status) return false;
      if (filter.q) {
        const q = filter.q.toLowerCase();
        const blob = [it.title, it.description, it.root_cause, it.lesson_learned, it.preventive_action, (it.tags || []).join(" ")].join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [items, filter]);

  return (
    <div data-testid="errors-page" style={{ maxWidth: 1180, margin: "0 auto" }}>
      {/* Hero / intro */}
      <div className="card vibrant-hero" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <h3 style={{ margin: "0 0 6px", color: "var(--brown-deep)", fontSize: 20 }}>
              🎓 الأخطاء والتعلم من الأخطاء
            </h3>
            <p style={{ margin: 0, fontSize: 12.5, color: "var(--muted)", lineHeight: 1.8, maxWidth: 720 }}>
              كل خطأ نسجّله هنا = درس محفوظ للأبد. النظام يستخرج <b style={{ color: "var(--brown-deep)" }}>الأنماط المتكرّرة</b>،
              يقترح Playbook وقائي، ويربط الأخطاء بالحملات والاجتماعات تلقائياً.
              <span style={{ background: "var(--gold-soft, #fef3c7)", padding: "2px 8px", borderRadius: 4, marginInline: 6 }}>💡 هذي أداة تتعلّم معك</span>
            </p>
          </div>
          {canCreate && (
            <button type="button" className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(EMPTY_FORM); }} data-testid="toggle-error-form">
              {showForm ? "إلغاء" : "+ سجّل خطأ"}
            </button>
          )}
        </div>
      </div>

      {/* Stats KPI strip */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginBottom: 14 }}>
          <div className="card kc kc-pro" style={{ padding: 12, textAlign: "center" }} data-testid="kpi-total-errors">
            <div style={{ fontSize: 26, fontWeight: 800, color: "var(--brown-deep)" }}>{fm(stats.total)}</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>إجمالي الأخطاء</div>
          </div>
          <div className="card kc" style={{ padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: "var(--danger)" }}>{fm(stats.open)}</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>🔴 مفتوحة</div>
          </div>
          <div className="card kc" style={{ padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: "var(--success)" }}>{fm(stats.lessons)}</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>📚 دروس مستفادة</div>
          </div>
          <div className="card kc" style={{ padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "var(--warn)" }}>{fm(stats.total_cost_sar)} ر</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>💸 الكلفة الإجمالية</div>
          </div>
          <div className="card kc" style={{ padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--brown-deep)" }}>
              {Object.entries(stats.by_shift || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || "—"}
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>🕐 أكثر شفت أخطاء</div>
          </div>
        </div>
      )}

      {/* View tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <button type="button" className={"btn " + (view === "list" ? "btn-primary" : "btn-soft")}
                onClick={() => setView("list")} data-testid="view-list">📋 السجل ({items.length})</button>
        <button type="button" className={"btn " + (view === "knowledge" ? "btn-primary" : "btn-soft")}
                onClick={() => setView("knowledge")} data-testid="view-knowledge">🎓 مكتبة المعرفة ({knowledge.length})</button>
        <button type="button" className={"btn " + (view === "ai" ? "btn-primary" : "btn-soft")}
                onClick={() => setView("ai")} data-testid="view-ai">🧠 تحليل ذكي</button>
        <button type="button" className="btn btn-soft" onClick={exportAll} data-testid="export-errors" style={{ marginInlineStart: "auto" }}>📥 تصدير CSV</button>
      </div>

      {/* Form */}
      {/* Form: when editing → admin only. When creating → any user. */}
      {showForm && (editingId ? canEdit : canCreate) && (
        <form className="card" onSubmit={submit} data-testid="error-form" style={{ marginBottom: 14 }}>
          <h4 style={{ margin: "0 0 10px", color: "var(--brown-deep)" }}>{editingId ? "✏ تعديل" : "➕ تسجيل خطأ جديد"}</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
            <div><label style={{ fontSize: 11 }}>العنوان *</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="err-title" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>التاريخ *</label><DateField  required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="err-date" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>التصنيف</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="err-cat" style={{ width: "100%" }}>
                {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11 }}>الخطورة</label>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="err-sev" style={{ width: "100%" }}>
                {Object.entries(SEVS).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11 }}>الحالة</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="err-status" style={{ width: "100%" }}>
                {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}
              </select>
            </div>
            <AssigneeSelect value={form.owner} onChange={(v) => setForm({ ...form, owner: v })} label="المسؤول" testid="err-owner" />
            <div><label style={{ fontSize: 11 }}>الشفت (اختياري)</label>
              <select value={form.linked_shift} onChange={(e) => setForm({ ...form, linked_shift: e.target.value })} style={{ width: "100%" }}>
                <option value="">— لا يخص شفت معين —</option>
                <option value="S1">شفت الصباح S1</option>
                <option value="S2">شفت العصر S2</option>
                <option value="S3">شفت المساء S3</option>
              </select>
            </div>
            <div><label style={{ fontSize: 11 }}>المنتج المرتبط (اختياري)</label><input value={form.linked_product} onChange={(e) => setForm({ ...form, linked_product: e.target.value })} style={{ width: "100%" }} placeholder="اسم المنتج إن وُجد" /></div>
            <div><label style={{ fontSize: 11 }}>الكلفة التقديرية (ر)</label><input type="number" min={0} value={form.cost_impact_sar} onChange={(e) => setForm({ ...form, cost_impact_sar: e.target.value })} style={{ width: "100%" }} /></div>
          </div>

          <div style={{ marginTop: 10 }}><label style={{ fontSize: 11 }}>الوصف</label>
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="err-desc" style={{ width: "100%" }} placeholder="ماذا حصل بالضبط؟" /></div>
          <div style={{ marginTop: 8 }}><label style={{ fontSize: 11 }}>🔎 السبب الجذري (Root Cause)</label>
            <textarea rows={2} value={form.root_cause} onChange={(e) => setForm({ ...form, root_cause: e.target.value })} data-testid="err-root" style={{ width: "100%" }} placeholder="ليش حصل؟ (اسأل ليش 5 مرات)" /></div>

          {/* 🧠 AI Similar Lessons — surfaces past wisdom BEFORE the user logs */}
          {!editingId && (form.title.trim() || form.description.trim()) && (
            <div style={{ marginTop: 10, padding: 10, background: "var(--info-soft, #eff6ff)", borderInlineStart: "3px solid var(--info, #3b82f6)", borderRadius: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <b style={{ color: "var(--info, #3b82f6)" }}>🧠 هل سبق وحصل خطأ مشابه؟</b>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                    دع Claude يبحث في الدروس المستفادة السابقة قبل ما تسجّل هذا الخطأ.
                  </div>
                </div>
                <button type="button" className="btn btn-soft" onClick={findSimilar} disabled={similarBusy} data-testid="err-find-similar">
                  {similarBusy ? "⏳ يبحث..." : "🔎 ابحث في الدروس المشابهة"}
                </button>
              </div>
              {similar && (
                <div style={{ marginTop: 8 }}>
                  {(similar.matches || []).length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <b style={{ fontSize: 12 }}>📚 دروس مشابهة:</b>
                      {similar.matches.map((m) => (
                        <div key={m.id} style={{ padding: 8, background: "#fff", borderRadius: 4, marginTop: 6, fontSize: 12, borderInlineStart: "2px solid var(--success)" }} data-testid={`similar-${m.id}`}>
                          <b>{m.title}</b> <span style={{ color: "var(--muted)", fontSize: 10 }}>· {m.date}</span>
                          <div style={{ marginTop: 4, color: "var(--brown-deep)" }}>📚 {m.lesson_learned}</div>
                          {m.preventive_action && <div style={{ marginTop: 2, fontSize: 11, color: "var(--brown)" }}>🛡 {m.preventive_action}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {similar.ai_advice && (
                    <div style={{ padding: 8, background: "#fff", borderRadius: 4, fontSize: 12.5, whiteSpace: "pre-wrap", lineHeight: 1.7 }} data-testid="similar-advice">
                      {similar.ai_advice}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 11, color: "var(--success)", fontWeight: 700 }}>📚 الدرس المستفاد *** (أهم حقل)</label>
            <textarea rows={3} value={form.lesson_learned} onChange={(e) => setForm({ ...form, lesson_learned: e.target.value })}
                      data-testid="err-lesson"
                      style={{ width: "100%", borderColor: "var(--success)", background: "var(--success-soft, #ecfdf5)" }}
                      placeholder="ما الذي تعلّمناه؟ صِغها كقاعدة عامة تنفع في المرات القادمة." />
          </div>
          <div style={{ marginTop: 8 }}><label style={{ fontSize: 11 }}>🛡 الإجراء الوقائي</label>
            <textarea rows={2} value={form.preventive_action} onChange={(e) => setForm({ ...form, preventive_action: e.target.value })} data-testid="err-prev" style={{ width: "100%" }} placeholder="ما الإجراء لمنع التكرار؟" /></div>
          <div style={{ marginTop: 8 }}><label style={{ fontSize: 11 }}>وسوم (مفصولة بفواصل)</label>
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="err-tags" style={{ width: "100%" }} placeholder="مثلاً: قهوة, ذروة, توصيل" />
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button type="submit" disabled={busy} className="btn btn-success" data-testid="err-submit">{busy ? "..." : (editingId ? "💾 حفظ التعديل" : "💾 احفظ الخطأ والدرس")}</button>
            <button type="button" className="btn btn-soft" onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}>إلغاء</button>
          </div>
          <p style={{ fontSize: 10, color: "var(--muted)", marginTop: 8 }}>
            💡 كل خطأ يُسجّل، الذكاء الاصطناعي يقرأه ويتعلم منه. كلما أضفت أكثر، التحليل يصير أدق.
          </p>
        </form>
      )}

      {/* LIST VIEW */}
      {view === "list" && (
        <>
          {/* Filters */}
          <div className="card" style={{ marginBottom: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
            <input placeholder="🔎 ابحث..." value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} data-testid="err-filter-q" />
            <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} data-testid="err-filter-cat">
              <option value="">كل التصنيفات</option>
              {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}
            </select>
            <select value={filter.severity} onChange={(e) => setFilter({ ...filter, severity: e.target.value })} data-testid="err-filter-sev">
              <option value="">كل الخطورات</option>
              {Object.entries(SEVS).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}
            </select>
            <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="err-filter-status">
              <option value="">كل الحالات</option>
              {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}
            </select>
          </div>

          {loading ? <div style={{ padding: 20, textAlign: "center" }}>⏳</div> : filtered.length === 0 ? (
            <div className="card" style={{ padding: 30, textAlign: "center", color: "var(--muted)" }}>
              {items.length === 0 ? (
                <>
                  <div style={{ fontSize: 32, marginBottom: 6 }}>📭</div>
                  <div>لا توجد أخطاء مسجّلة بعد.</div>
                  <div style={{ fontSize: 12, marginTop: 6 }}>ابدأ بتسجيل أول خطأ — حتى الصغير منها يصير درساً يفيد فريقك.</div>
                </>
              ) : "لا توجد نتائج تطابق الفلاتر"}
            </div>
          ) : filtered.map((it) => {
            const cat = CATS[it.category] || CATS.other;
            const sev = SEVS[it.severity] || SEVS.medium;
            const st = STATUSES[it.status] || STATUSES.open;
            return (
              <div key={it.id} className="card err-card" style={{ marginBottom: 10, borderInlineStart: `4px solid ${sev.color}` }} data-testid={`err-${it.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ background: cat.color, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 10 }}>{cat.lbl}</span>
                      <span style={{ background: sev.color, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 10 }}>{sev.lbl}</span>
                      <span style={{ background: st.color, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 10 }}>{st.lbl}</span>
                      {it.linked_shift && <span style={{ background: "var(--brown)", color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 10 }}>🕐 {it.linked_shift}</span>}
                      {it.cost_impact_sar > 0 && <span style={{ color: "var(--danger)", fontSize: 11, fontWeight: 700 }}>💸 {fm(it.cost_impact_sar)} ر</span>}
                    </div>
                    <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>{it.title}</h4>
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>📅 {it.date} {it.owner && `· 👤 ${it.owner}`}</div>
                    {it.description && <p style={{ margin: "6px 0 0", fontSize: 12.5 }}>{it.description}</p>}
                    {it.root_cause && <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--brown)" }}>🔎 <b>السبب:</b> {it.root_cause}</p>}
                    {it.lesson_learned && (
                      <div style={{ marginTop: 8, padding: 10, background: "var(--success-soft, #ecfdf5)", borderInlineStart: "3px solid var(--success)", borderRadius: 4 }}>
                        <b style={{ color: "var(--success)" }}>📚 الدرس المستفاد:</b>
                        <div style={{ marginTop: 4, fontSize: 12.5 }}>{it.lesson_learned}</div>
                      </div>
                    )}
                    {it.preventive_action && <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--brown-deep)" }}>🛡 <b>الإجراء الوقائي:</b> {it.preventive_action}</p>}
                    {(it.tags || []).length > 0 && (
                      <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {it.tags.map((t) => <span key={t} style={{ background: "var(--beige)", color: "var(--brown)", padding: "2px 8px", borderRadius: 10, fontSize: 10 }}>#{t}</span>)}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {canEdit && (
                      <>
                        <select value={it.status} onChange={(e) => setStatus(it, e.target.value)} style={{ fontSize: 11 }} data-testid={`err-status-${it.id}`}>
                          {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}
                        </select>
                        <button type="button" className="btn btn-soft" onClick={() => startEdit(it)} data-testid={`err-edit-${it.id}`} style={{ fontSize: 11 }}>✏ تعديل</button>
 <button type="button" className="btn btn-soft" onClick={() => convertToTask(it)} disabled={meetingsList.length === 0} style={{ fontSize: 11 }} data-testid={`err-to-task-${it.id}`}>📋 → مهمة</button>
                      </>
                    )}
                    {canDelete && (
                      <button type="button" onClick={() => remove(it)} className="btn" data-testid={`err-delete-${it.id}`} style={{ background: "var(--danger)", color: "#fff", fontSize: 11 }} aria-label={meetingsList.length === 0 ? "أنشئ اجتماعاً أولاً" : ""}>🗑</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* KNOWLEDGE BASE VIEW */}
      {view === "knowledge" && (
        <div>
          <div className="card" style={{ marginBottom: 10, background: "var(--success-soft, #ecfdf5)", borderInlineStart: "3px solid var(--success)" }}>
            <h4 style={{ margin: "0 0 4px", color: "var(--success)" }}>🎓 مكتبة المعرفة</h4>
            <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>
              كل الدروس المستفادة من الأخطاء السابقة — قاعدة معرفة فريقك. يمكن البحث + التصدير.
            </p>
          </div>
          {knowledge.length === 0 ? (
            <div className="card" style={{ padding: 30, textAlign: "center", color: "var(--muted)" }}>
              لا توجد دروس مستفادة بعد. سجّل أخطاء مع تعبئة حقل «الدرس المستفاد».
            </div>
          ) : knowledge.map((k) => {
            const cat = CATS[k.category] || CATS.other;
            return (
              <div key={k.id} className="card" style={{ marginBottom: 8 }} data-testid={`kb-${k.id}`}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <span style={{ background: cat.color, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 10 }}>{cat.lbl}</span>
                  <b style={{ color: "var(--brown-deep)" }}>{k.title}</b>
                  <span style={{ fontSize: 11, color: "var(--muted)" }}>· {k.date}</span>
                </div>
                <div style={{ padding: 8, background: "var(--card)", borderRadius: 4, fontSize: 13 }}>
                  📚 {k.lesson_learned}
                </div>
                {k.preventive_action && <div style={{ marginTop: 4, fontSize: 12, color: "var(--brown)" }}>🛡 {k.preventive_action}</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* AI ANALYSIS VIEW */}
      {view === "ai" && (
        <div>
          <div className="card" style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <h4 style={{ margin: "0 0 4px", color: "var(--brown-deep)" }}>🧠 تحليل ذكي بواسطة Claude</h4>
                <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>
                  يحلل سجل الأخطاء، يكتشف الأنماط المتكررة، ويقترح Playbook وقائي.
                </p>
                {aiGeneratedAt && <p style={{ margin: "4px 0 0", fontSize: 10, color: "var(--muted)" }}>آخر تحليل: {formatKSA(new Date(aiGeneratedAt))}</p>}
              </div>
              <button type="button" className="btn btn-primary" onClick={runAI} disabled={aiBusy} data-testid="err-ai-run">
                {aiBusy ? "⏳ يحلل..." : (aiText ? "🔄 تحليل جديد" : "🚀 ابدأ التحليل")}
              </button>
            </div>
          </div>
          {aiBusy && <div className="card" style={{ padding: 30, textAlign: "center" }}>⏳ يجمع البيانات ويحلل... (15-30 ثانية)</div>}
          {aiText && (
            <div className="card" data-testid="err-ai-output" style={{ whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.85 }}>
              {aiText}
            </div>
          )}
          {!aiBusy && !aiText && items.length > 0 && (
            <div className="card" style={{ padding: 30, textAlign: "center", color: "var(--muted)" }}>
              اضغط «ابدأ التحليل» لاستخراج الأنماط والدروس من سجلّك.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
